import { ModalContext } from '#app/components/Modals/V2/context.js';
import { ModalSize } from '#app/components/Modals/V2/types.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ProBadge } from '#app/components/ProBadge/ProBadge.js';
import {
  BetaTag,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerScreen,
  DrawerWrapper,
} from './SlideUpDrawerV2.styles.js';
import { CloseIcon } from '@contra/icons/icons';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { OverlayContainer } from '@react-aria/overlays';
import { forwardRef, type ReactNode, type Ref, useMemo, useRef } from 'react';
import { css, styled } from 'styled-components';

type CloseIconColor = 'black' | 'white';

type DrawerProps = {
  readonly ariaLabel?: string;
  readonly betaTag?: boolean;
  readonly children: ReactNode;
  readonly closeIconColor?: CloseIconColor;
  readonly customHeader?: ReactNode;
  readonly darkMode?: boolean;
  readonly drawerIcon?: ReactNode;
  readonly drawerLabel?: ReactNode | string;
  readonly headerHero?: React.ReactNode;
  readonly hideCloseIcon?: boolean;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly preventFocusContain?: boolean;
  readonly proTag?: boolean;
  readonly removePaddingOnMobile?: boolean;
  readonly removePaddingX?: boolean;
  readonly size?: ModalSize;
};

const StyledCloseIcon = styled(CloseIcon)<{ $color?: CloseIconColor }>`
  ${({ $color, theme }) => css`
    color: ${$color ? theme.colorsV2[$color] : undefined};
  `}
`;

export const SlideUpDrawerV2 = forwardRef(
  (
    {
      ariaLabel,
      betaTag = false,
      children,
      closeIconColor,
      customHeader,
      drawerIcon,
      drawerLabel,
      headerHero,
      hideCloseIcon,
      isOpen,
      onClose,
      preventFocusContain,
      proTag = false,
      removePaddingOnMobile,
      removePaddingX,
      size = 'lg',
      ...drawerProps
    }: DrawerProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const { titleProps } = useDialog(
      { ...drawerProps, 'aria-label': ariaLabel, role: 'dialog' },
      dialogRef,
    );

    const context = useMemo(() => {
      return { isDismissible: false, onClose, size, titleProps };
    }, [onClose, size, titleProps]);

    return isOpen ? (
      <ModalContext.Provider value={context}>
        <OverlayContainer>
          <FocusScope
            autoFocus
            contain={!preventFocusContain}
            restoreFocus
          >
            <DrawerScreen>
              <DrawerWrapper
                isOpen={isOpen}
                ref={ref}
                role="dialog"
                {...drawerProps}
              >
                {customHeader}
                {headerHero}

                <DrawerContent
                  ref={dialogRef}
                  removePaddingOnMobile={removePaddingOnMobile}
                >
                  {betaTag ? <BetaTag>Beta</BetaTag> : null}
                  {proTag ? <ProBadge /> : null}
                  <DrawerHeader>
                    {drawerIcon}
                    {drawerLabel ? (
                      <Text
                        as="h6"
                        textStyle="sixthLevelHeadline"
                      >
                        {drawerLabel}
                      </Text>
                    ) : null}
                    {hideCloseIcon ? null : (
                      <DrawerCloseButton
                        aria-label="Close"
                        isAbsolutePositioned={Boolean(headerHero)}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onClose();
                        }}
                        type="button"
                      >
                        <StyledCloseIcon
                          $color={closeIconColor}
                          size={24}
                        />
                      </DrawerCloseButton>
                    )}
                  </DrawerHeader>
                  <DrawerBody removePaddingX={removePaddingX}>
                    {children}
                  </DrawerBody>
                </DrawerContent>
              </DrawerWrapper>
            </DrawerScreen>
          </FocusScope>
        </OverlayContainer>
      </ModalContext.Provider>
    ) : null;
  },
);
